import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory, useLocation } from "react-router-dom";
import {
  getQuantityGrid,
  resetQuantityGrid
} from "actions/quantityGridActions.js";
import {
  requestQtyGrid as getStockModalQuantityGrid,
  resetQuantityGrid as resetStockModalQtyGrid
} from "actions/StockModal/qtyGridActions.js";

import {
  findByProductReleaseId,
  findByStockModalItemId
} from "selectors/productsSelector.js";

import { productReleaseDataSelector as productReleaseFromDetailsSelector } from "selectors/productReleaseSelector.js";

import {
  isLoadingSelector,
  productReleaseSelector,
  deliveryMonthsSelector,
  attributeSeqSelector,
  headersSelector,
  atsSelector,
  poDatesSelector,
  preOrderDeliveryMonthSelector,
  isPreOrderProductSelector,
  preOrderMessageSelector,
  sizeMinsSelector,
  bpsbToHighlightSelector
} from "selectors/quantityGridSelector.js";

import { buyplanIdSelector } from "selectors/buyplanIdSelector.js";

import { warehouseIdSelector } from "selectors/warehouseSelector";

import { CONSIGNMENT } from "constants.js";

import QuantityGrid from "components/QuantityGrid/index.jsx";

const QuantityGridContainer = ({ buyplanState, productReleaseId: propProductReleaseId, buyplanItemId: propBuyplanItemId, isModalContext = false }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const stockModalItemId = queryParams.get("stockModalItemId");
  const params = useParams();
  const { stockModalId } = useParams();

  let productReleaseId, buyplanItemIdFromParams, buyplanItemId;

  // Use props if provided, otherwise get from URL parameters
  if (propProductReleaseId) {
    productReleaseId = propProductReleaseId;
    buyplanItemId = propBuyplanItemId;
  } else if (stockModalId) {
    if (location.pathname.includes("quick-view")) {
      productReleaseId = params?.productReleaseId;
    } else {
      productReleaseId = queryParams.get("productReleaseId");
    }
  } else {
    productReleaseId = params.productReleaseId;
    buyplanItemIdFromParams = queryParams.get("buyplanItemId");
  }

  const buyplanIdFromRedux = useSelector(buyplanIdSelector);
  const warehouseId = useSelector(warehouseIdSelector);
  
  // Extract buyplan ID from URL as fallback when Redux state is null
  // URL pattern: /releases/3823/products/for/1344240/whiteboard/OCT26/page1
  let buyplanId = buyplanIdFromRedux;
  if (!buyplanId || buyplanId === "null" || buyplanId === "undefined") {
    const pathParts = location.pathname.split('/');
    const forIndex = pathParts.indexOf('for');
    if (forIndex !== -1 && pathParts[forIndex + 1]) {
      buyplanId = pathParts[forIndex + 1];
    }
  }

  const productReleaseFromList = useSelector(
    productReleaseId
      ? findByProductReleaseId(productReleaseId)
      : findByStockModalItemId(stockModalItemId)
  );

  const productReleaseFromDetails = useSelector(
    productReleaseFromDetailsSelector
  );

  const productRelease =
    productReleaseFromList || productReleaseFromDetails?.toJS();
  // Only determine buyplanItemId from URL params if not provided as prop
  if (!propBuyplanItemId) {
    if (
      buyplanItemIdFromParams &&
      buyplanItemIdFromParams !== "undefined" &&
      buyplanItemIdFromParams !== "null"
    ) {
      buyplanItemId = buyplanItemIdFromParams;
    } else if (productRelease && productRelease.buyplan_item) {
      buyplanItemId = productRelease.buyplan_item.id;
    }
  }

  const history = useHistory();

  const ats = useSelector(atsSelector);
  const poDates = useSelector(poDatesSelector);

  const closeModalCallback = useCallback(() => {
    const path = history.location.pathname;
    if (path.includes("/stock-modal-items")) {
      return history.push(
        history.location.pathname.split("/stock-modal-items")[0]
      );
    } else if (buyplanState === CONSIGNMENT) {
      return history.push(
        history.location.pathname.split(`/${productReleaseId}`)[0] +
          "?type=consignment"
      );
    } else {
      return history.push(
        history.location.pathname.split(`/${productReleaseId}`)[0]
      );
    }
  }, [history, productReleaseId, buyplanState]);

  const isLoading = useSelector(isLoadingSelector);
  const qtyGridProductRelease = useSelector(productReleaseSelector);
  const deliveryMonths = useSelector(deliveryMonthsSelector);
  const attrsList = useSelector(attributeSeqSelector);
  const headers = useSelector(headersSelector);
  const sizeMinimums = useSelector(sizeMinsSelector);
  const pre_order_month_and_date = useSelector(preOrderDeliveryMonthSelector);
  const is_pre_order_product = useSelector(isPreOrderProductSelector);
  const pre_order_message = useSelector(preOrderMessageSelector);
  const bpsbToHighlight = useSelector(bpsbToHighlightSelector);

  useEffect(() => {
    const path = history.location.pathname;

    if (!path.includes("/stock-modal-items") && productReleaseId && buyplanId) {
      dispatch(
        getQuantityGrid({
          productReleaseId,
          buyplanId,
          buyplanItemId,
          warehouse_id: warehouseId
        })
      );

      return () => {
        // Don't reset quantity grid data when used in modal context to preserve buyplanItem data
        if (!isModalContext) {
          dispatch(resetQuantityGrid());
        }
      };
    }
  }, [
    dispatch,
    productReleaseId,
    buyplanId,
    buyplanItemId,
    warehouseId,
    history,
    isModalContext
  ]);

  useEffect(() => {
    const path = history.location.pathname;

    if (path.includes("/stock_models/")) {
      dispatch(
        getStockModalQuantityGrid({
          stockModalId,
          stockModalItemId,
          productReleaseId,
          warehouseId
        })
      );

      return () => {
        // Don't reset quantity grid data when used in modal context to preserve buyplanItem data
        if (!isModalContext) {
          dispatch(resetStockModalQtyGrid());
        }
      };
    }
  }, [
    dispatch,
    stockModalId,
    stockModalItemId,
    productReleaseId,
    history,
    warehouseId,
    isModalContext
  ]);

  return (
    <QuantityGrid
      isLoading={isLoading}
      title={productRelease && productRelease.product.name}
      deliveryMonths={deliveryMonths}
      filledMonths={productRelease && productRelease.buyplan_item.filled_months}
      attrsList={attrsList}
      headers={headers}
      sizeMinimums={sizeMinimums}
      intendedMonths={
        productRelease && productRelease.buyplan_item.intended_months
      }
      qtyGridProductRelease={qtyGridProductRelease}
      ats={ats}
      poDates={poDates}
      buyplanItemId={buyplanItemId}
      closeModalCallback={closeModalCallback}
      shouldFetchTotals={false}
      pre_order_month_and_date={[pre_order_month_and_date].filter(obj => obj)}
      is_pre_order_product={is_pre_order_product}
      pre_order_message={pre_order_message}
      preOrderIntendedMonths={productRelease && productRelease.buyplan_item.pre_order_intended_months}
      preOrderFilledMonths={productRelease && productRelease.buyplan_item.pre_order_filled_months}
      bpsbToHighlight={bpsbToHighlight}
    />
  );
};

export default React.memo(QuantityGridContainer);
